import React, { useMemo, useState } from 'react';

import {
  balancesAfterImport,
  buildImportPayload,
  exceedsImportPayloadLimit,
  exceedsPreviewRatePairs,
  formatSignedCurrency,
  importOverFreeLimit,
  ratePairs,
  reconciledImportNets,
} from '@splitfiesta/core';
import type { ImportReviewState, SplitwiseFile } from '@splitfiesta/core';
import { useNamespace, useTranslation } from '@splitfiesta/i18n';
import type { SplitwiseImportRequestDto } from '@splitfiesta/shared';
import { Alert, Avatar, Button, FormCheckbox } from '@splitfiesta/ui';

import { ActionButtonGroup } from '@/components/ui/ActionButtonGroup';

import { useExchangeRatesQuery } from '@/hooks/queries/useExchangeRateQuery';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';

import { useAuthStore } from '@/stores/authStore';

import { ImportCard } from './ui/ImportCard';
import { ImportSection } from './ui/ImportSection';

interface Blocker {
  key: string;
  params?: Record<string, unknown>;
  /** A second line under the reason, for what lifts it. */
  noteKey?: string;
}

interface Props {
  file: SplitwiseFile;
  review: ImportReviewState;
  submitting: boolean;
  error: { key: string; params?: Record<string, unknown> } | null;
  onBack: () => void;
  onConfirm: (payload: SplitwiseImportRequestDto) => void;
}

/** Green owed, red owing — the colours every balance in the app already uses. */
function BalanceAmount({
  amount,
  currency,
}: {
  amount: number;
  currency: string;
}) {
  const tone =
    amount > 0
      ? 'text-green-600 dark:text-green-400'
      : amount < 0
        ? 'text-red-600 dark:text-red-400'
        : 'text-gray-500 dark:text-gray-400';
  return (
    <span className={`text-base font-bold ${tone}`}>
      {formatSignedCurrency(amount, currency)}
    </span>
  );
}

export function ImportConfirmStep({
  file,
  review,
  submitting,
  error,
  onBack,
  onConfirm,
}: Props) {
  const { t } = useTranslation();
  useNamespace('import');
  const { isOnline } = useNetworkStatus();
  const userPro = useAuthStore(state => state.user?.pro);
  const [acknowledged, setAcknowledged] = useState(false);

  const payload = useMemo(
    () => buildImportPayload(file, review),
    [file, review]
  );

  const blocker = useMemo<Blocker | null>(() => {
    // Both limits are re-checked on the built payload rather than on the file:
    // giving the zero-net rows to a member adds expenses the summary step may
    // not have counted yet, and only the encoded body knows how big the
    // request really is.
    const overLimit = importOverFreeLimit({
      expenseRows: payload.rows.filter(row => row.kind !== 'payment').length,
      userPro,
    });
    if (overLimit) {
      return {
        key: 'import:summary.overLimit',
        params: { limit: overLimit.limit },
        noteKey: 'import:confirm.overLimitFree',
      };
    }
    if (exceedsImportPayloadLimit(payload)) {
      return { key: 'import:errors.payloadTooLarge' };
    }
    return null;
  }, [payload, userPro]);

  // React Query would pause an offline mutation and replay it later, which for
  // an import means a second group appearing long after the user gave up.
  const reason: Blocker | null =
    blocker ?? (isOnline ? null : { key: 'import:errors.offline' });

  const pairs = useMemo(
    () => ratePairs(payload.rows, payload.groupCurrency),
    [payload]
  );
  const rateFor = useExchangeRatesQuery({
    pairs,
    to: payload.groupCurrency,
    enabled: isOnline,
  });
  // The very nets the import will store, so the preview cannot drift from it.
  const reconciled = useMemo(
    () => reconciledImportNets(payload.rows, payload.groupCurrency, rateFor),
    [payload, rateFor]
  );
  const balances = useMemo(
    () =>
      balancesAfterImport(
        reconciled,
        payload.groupCurrency,
        review.memberNames.length
      ),
    [reconciled, payload.groupCurrency, review.memberNames.length]
  );
  // Only an open foreign currency can drift: a settled one is converted at a
  // single rate, so it nets to zero after the import as it did in Splitwise.
  const openCurrencies = reconciled.dates.openCurrencies;
  const needsAcknowledgement = openCurrencies.length > 0;
  // Nothing is on its way in either case, so "waiting for rates" would be a
  // lie: offline the query never runs, and past the pair cap the preview
  // deliberately asks for nothing.
  const ratesUnavailable =
    !isOnline || exceedsPreviewRatePairs(payload.rows, payload.groupCurrency);

  const payments = payload.rows.filter(row => row.kind === 'payment').length;
  const expenses = payload.rows.length - payments;

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
        {t('import:confirm.title')}
      </h2>

      <p
        data-testid="import-confirm-summary"
        className="text-sm text-gray-600 dark:text-gray-300"
      >
        {/* The counts are translated on their own so each reads correctly at
            one, which a single sentence with three plurals cannot. The
            sentence is split around the currency because it is the one part
            that cannot be changed after the import, and no component
            interpolation is wired up in this codebase. */}
        {t('import:confirm.summaryBefore', {
          expenses: t('import:confirm.expenses', { count: expenses }),
          payments: t('import:confirm.payments', { count: payments }),
        })}{' '}
        <strong
          data-testid="import-confirm-summary-currency"
          className="font-semibold text-gray-900 dark:text-white"
        >
          {review.groupCurrency}
        </strong>{' '}
        {t('import:confirm.summaryAfter', {
          members: t('import:confirm.members', {
            count: review.memberNames.length,
          }),
        })}
      </p>

      <ImportSection title={t('import:confirm.balancesTitle')}>
        <ImportCard data-testid="import-confirm-balances">
          {balances === null ? (
            <p
              data-testid={
                ratesUnavailable
                  ? 'import-confirm-rates-unavailable'
                  : 'import-confirm-rates-pending'
              }
              className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400"
            >
              {t(
                ratesUnavailable
                  ? 'import:confirm.balancesUnavailable'
                  : 'import:confirm.ratesPending'
              )}
            </p>
          ) : (
            <ul className="divide-y divide-gray-100 dark:divide-gray-700">
              {review.memberNames.map((name, column) => (
                <li
                  key={column}
                  data-testid={`import-confirm-balance-${column}`}
                  className="flex items-center gap-3 px-4 py-2.5 text-sm"
                >
                  <Avatar name={name} size="sm" className="shrink-0" />
                  <span className="min-w-0 flex-1 truncate text-gray-900 dark:text-gray-100">
                    {name}
                  </span>
                  <BalanceAmount
                    amount={balances[column] ?? 0}
                    currency={payload.groupCurrency}
                  />
                </li>
              ))}
            </ul>
          )}
        </ImportCard>
      </ImportSection>

      {needsAcknowledgement && (
        <>
          <Alert
            type="info"
            data-testid="import-confirm-residual"
            message={t('import:confirm.residualNote', {
              currencies: openCurrencies.join(', '),
              currency: payload.groupCurrency,
            })}
          />
          <FormCheckbox
            id="import-confirm-ack"
            data-testid="import-confirm-ack"
            checked={acknowledged}
            onChange={setAcknowledged}
            label={t('import:confirm.acknowledge', {
              currency: payload.groupCurrency,
            })}
          />
        </>
      )}

      {error && (
        <Alert
          type="error"
          message={t(error.key, error.params)}
          data-testid="import-confirm-error"
        />
      )}

      {reason && (
        <Alert
          type={blocker ? 'error' : 'warning'}
          message={
            reason.noteKey ? (
              <>
                <p>{t(reason.key, reason.params)}</p>
                <p>{t(reason.noteKey)}</p>
              </>
            ) : (
              t(reason.key, reason.params)
            )
          }
          data-testid="import-confirm-blocker"
        />
      )}

      <ActionButtonGroup>
        <Button
          data-testid="import-confirm-back"
          variant="secondary"
          onClick={onBack}
        >
          {t('import:confirm.back')}
        </Button>
        <Button
          data-testid="import-confirm-start"
          loading={submitting}
          disabled={
            submitting ||
            reason !== null ||
            (needsAcknowledgement && !acknowledged)
          }
          onClick={() => onConfirm(payload)}
        >
          {t('import:confirm.cta')}
        </Button>
      </ActionButtonGroup>
    </div>
  );
}
